import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import styled, { useTheme } from 'styled-components';
import { useDropzone } from 'react-dropzone';
import Meta from 'src/components/Meta';
import { AssessmentCardSkeleton, BackSvg, GradientText, InputLabel, PageContainer, Poppins, Spacer } from 'src/common';
import Button from 'src/components/form/Button';
import AppCheckBox from 'src/components/form/CheckBox';
import Input, { Textarea } from 'src/components/form/Input';
import Select, { CreatableSelect } from 'src/components/form/Select';
import { Modal } from 'src/components/modals/comps';
import { Tile, TileDescription, TileDescriptionContainer } from 'src/components/Tile';
import {
  GTExpander,
  GTSearch,
  ScenarioLibraryItem,
  TableSheetControlLibrary,
  TableSheetScenarioLibrary,
} from 'src/components/GenericTable';
import CommonMenu from 'src/components/CommonMenu';
import { ThreeDots } from 'src/components/CommonMenu/comps';
import type { CallbackType } from 'src/components/CommonMenu/types';
import { ConfirmDeleteModal } from 'src/components/modals/ConfirmDeleteModal';
import { showErrorToast, showSuccessToast } from 'src/components/Toast';
import { CsvValidationErrors, SCVSummary } from 'src/components/CsvUpload';
import ErrorPage from 'src/pages/ErrorPage';
import { ScenarioGroupVersionHistory } from 'src/pages/ScenarioLibraryPage/ScenarioGroupVersionHistory';
import { AddControlFrameworkModal } from 'src/pages/FrameworkLibrariesPage/AddControlFrameworkModal';
import { EditControlFrameworkModal } from 'src/pages/FrameworkLibrariesPage/EditControlFrameworkModal';
import {
  ControlFrameworkSummary,
  ControlFrameworkVersionHistory,
} from 'src/pages/FrameworkLibrariesPage/ControlFrameworkVersionHistory';
import { ScenarioGroupSummary } from 'src/graphql';
import {
  CONTENT_MANAGEMENT_CONTROL_FRAMEWORKS,
  CONTENT_MANAGEMENT_SCENARIO_PACKS,
  ContentManagementControlFrameworkSummary,
  ContentManagementControlFrameworksQuery,
  ContentManagementScenarioPackSummary,
  ContentManagementScenarioPacksQuery,
  ContentPackOwnerOrganisation,
} from 'src/graphql/contentManagement';
import {
  CONTENT_FEATURES,
  CREATE_SCENARIO_GROUP_FROM_IMPORT,
  DELETE_CONTROL_FRAMEWORK,
  DELETE_CONTROL_FRAMEWORK_CONTROLS,
  DELETE_SCENARIO_GROUP_PACK,
  DELETE_SCENARIO_GROUP_RISKS,
  ContentFeaturesQuery,
  DeleteControlFrameworkControlsMutation,
  DeleteControlFrameworkControlsMutationVariables,
  DeleteControlFrameworkMutation,
  DeleteControlFrameworkMutationVariables,
  DeleteScenarioGroupPackMutation,
  DeleteScenarioGroupPackMutationVariables,
  DeleteScenarioGroupRisksMutation,
  DeleteScenarioGroupRisksMutationVariables,
  IMPORT_SCENARIO_GROUP,
  ImportScenarioGroupMutation,
  ImportScenarioGroupMutationVariables,
  UPDATE_SCENARIO_GROUP_METADATA,
  UPSERT_CONTROL_FRAMEWORK_FEATURE,
  UPSERT_SCENARIO_PACK_FEATURE,
  UpsertControlFrameworkFeatureMutation,
  UpsertControlFrameworkFeatureMutationVariables,
  UpsertScenarioPackFeatureMutation,
  UpsertScenarioPackFeatureMutationVariables,
  CreateScenarioGroupFromImportMutation,
  CreateScenarioGroupFromImportMutationVariables,
  UpdateScenarioGroupMetadataMutation,
  UpdateScenarioGroupMetadataMutationVariables,
} from 'src/graphql/contentImport';
import { ScenarioPackImportPayload } from 'src/types/contentImport';
import { useCanManageContent } from 'src/hooks/useCanManageContent';
import { useAuth } from 'src/state/auth';
import { useFlatOrganisationsHomeQuery } from 'src/graphql/_generated';
import useWorkspaceVariables from 'src/hooks/useWorkspaceVariables';
import { ScenarioSvg } from 'src/assets/dynamicSvg';
import cydeaLogo from 'src/assets/cydea.svg';
import { formatDate } from 'src/utils/misc';
import { parseGraphqlCsvValidationErrors, ValidationErrorItem } from 'src/utils/csv';
import { generateScenarioPackTemplateLink, parseScenarioPackCsv } from 'src/pages/ScenarioLibraryPage/import';
import { downloadScenarioGroupCsv } from 'src/pages/ScenarioLibraryPage/export';
import { getPlanTypeLabel, getRiskConsequencesName, getRiskEventsName, getRiskSourcesName } from 'src/api/types';

type Tab = 'scenarios' | 'frameworks' | 'wizards';
type PackStatus = 'draft' | 'published';

type PublishTargetOrgUnit = {
  id: string;
  name: string;
  parentId?: string | null;
  recordType: string;
  isArchived: boolean;
  logoUrl?: string | null;
  organisationType?: string | null;
  plan?: string | null;
  planType?: Parameters<typeof getPlanTypeLabel>[0];
};

type ScenarioPackWithDetails = Omit<ContentManagementScenarioPackSummary, 'scenarios'> & {
  scenarios: Array<{
    id: string;
    name?: string | null;
    description?: string | null;
    ownerName?: string | null;
    source?: number | null;
    event?: number | null;
    consequence?: number | null;
    riskNarrative?: string | null;
    frequencyNote?: string | null;
    managementStrategy?: string | null;
    managementNote?: string | null;
    consequenceNote?: string | null;
    lowerFormula?: string | null;
    upperFormula?: string | null;
    status?: string | null;
    riskIdentifiedDate?: string | null;
    reviewDate?: string | null;
    assessment?: { id: string } | null;
    baseRisk?: {
      frequencyTimes?: number | null;
      frequencyYears?: number | null;
      expectedLoss?: number | null;
      lower?: number | null;
      upper?: number | null;
    } | null;
    projectedRisk?: {
      frequencyTimes?: number | null;
      frequencyYears?: number | null;
      expectedLoss?: number | null;
      lower?: number | null;
      upper?: number | null;
    } | null;
    liveRisk?: {
      frequencyTimes?: number | null;
      frequencyYears?: number | null;
      expectedLoss?: number | null;
      lower?: number | null;
      upper?: number | null;
    } | null;
    meta?: { createdAt?: string | null; updatedAt?: string | null } | null;
  }>;
};

const scenarioPackFeatureKey = (groupId: string) => `scenario-pack.custom.${groupId.trim().toLowerCase()}`;
const controlFrameworkFeatureKey = (frameworkId: string) => `control-framework.custom.${frameworkId.trim().toLowerCase()}`;
const contentFeatureKey = (
  type: 'scenario' | 'framework',
  item?: ScenarioGroupSummary | ControlFrameworkSummary | null,
) => {
  if (!item) return '';
  return type === 'scenario' ? item.featureKey || scenarioPackFeatureKey(item.id) : item.featureKey || controlFrameworkFeatureKey(item.id);
};

const ContentEditIcon = () => (
  <svg width="18" height="18" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path
      d="M4 23.9333V28H8.06667L25.9867 10.08L21.92 6.01333L4 23.9333Z"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M19.8667 8.06667L23.9333 4C25.0579 2.87543 26.8779 2.87543 28.0025 4L28 4.0025C29.1246 5.12707 29.1246 6.94707 28 8.07164L23.9333 12.1383"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M19.8667 8.06665L23.9333 12.1333"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ContentDeleteIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path
      d="M5 2C5 1.46957 5.21071 0.960859 5.58579 0.585786C5.96086 0.210714 6.46957 0 7 0H13C13.5304 0 14.0391 0.210714 14.4142 0.585786C14.7893 0.960859 15 1.46957 15 2V4H19C19.2652 4 19.5196 4.10536 19.7071 4.29289C19.8946 4.48043 20 4.73478 20 5C20 5.26522 19.8946 5.51957 19.7071 5.70711C19.5196 5.89464 19.2652 6 19 6H17.931L17.064 18.142C17.0281 18.6466 16.8023 19.1188 16.4321 19.4636C16.0619 19.8083 15.5749 20 15.069 20H4.93C4.42414 20 3.93707 19.8083 3.56688 19.4636C3.1967 19.1188 2.97092 18.6466 2.935 18.142L2.07 6H1C0.734784 6 0.48043 5.89464 0.292893 5.70711C0.105357 5.51957 0 5.26522 0 5C0 4.73478 0.105357 4.48043 0.292893 4.29289C0.48043 4.10536 0.734784 4 1 4H5V2ZM7 4H13V2H7V4ZM4.074 6L4.931 18H15.07L15.927 6H4.074ZM8 8C8.26522 8 8.51957 8.10536 8.70711 8.29289C8.89464 8.48043 9 8.73478 9 9V15C9 15.2652 8.89464 15.5196 8.70711 15.7071C8.51957 15.8946 8.26522 16 8 16C7.73478 16 7.48043 15.8946 7.29289 15.7071C7.10536 15.5196 7 15.2652 7 15V9C7 8.73478 7.10536 8.48043 7.29289 8.29289C7.48043 8.10536 7.73478 8 8 8ZM12 8C12.2652 8 12.5196 8.10536 12.7071 8.29289C12.8946 8.48043 13 8.73478 13 9V15C13 15.2652 12.8946 15.5196 12.7071 15.7071C12.5196 15.8946 12.2652 16 12 16C11.7348 16 11.4804 15.8946 11.2929 15.7071C11.1054 15.5196 11 15.2652 11 15V9C11 8.73478 11.1054 8.48043 11.2929 8.29289C11.4804 8.10536 11.7348 8 12 8Z"
      fill="currentColor"
    />
  </svg>
);

const Div = styled(PageContainer)`
  padding-top: 52px;

  .title-row {
    display: flex;
    justify-content: space-between;
    gap: 24px;
    align-items: flex-start;
  }

  .tabs {
    display: flex;
    align-items: center;
    gap: 38px;
    border-bottom: 1px solid ${({ theme }) => theme.colors.stroke};
    margin-top: 34px;
  }

  .tab {
    border: 0;
    background: transparent;
    padding: 0 0 13px;
    color: ${({ theme }) => theme.colors.cflowerBlue};
    cursor: pointer;
    font-family: inherit;
    font-size: 18px;
    line-height: 24px;
    font-weight: 600;
  }

  .tab--active {
    color: ${({ theme }) => theme.colors.prussianBlue};
    border-bottom: 3px solid ${({ theme }) => theme.colors.gp1};
  }

  ${({ theme }) => theme.breakpoints.down('md')} {
    .title-row {
      flex-direction: column;
    }

    .tabs {
      gap: 26px;
    }
  }
`;

const ActionButton = styled(Button)`
  width: 238px;
  min-width: 238px;
  height: 44px;
  padding-left: 1.5rem;
  padding-right: 1.5rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  line-height: 20px;

  ${({ theme }) => theme.breakpoints.down('sm')} {
    width: 100%;
    min-width: 0;
  }
`;

const AccessConfigButton = styled(Button)`
  width: 243px;
  min-width: 243px;
  height: 50px;
  padding-left: 1.25rem;
  padding-right: 1.25rem;
  font-size: 14px;
  line-height: 20px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
  margin-top: 28px;
  margin-bottom: 38px;

  ${({ theme }) => theme.breakpoints.down('md')} {
    flex-direction: column;
    margin-bottom: 24px;
  }
`;

const CardsGrid = styled.div`
  display: grid;
  grid-gap: 20px;
  grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));

  ${({ theme }) => theme.breakpoints.down('sm')} {
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  }
`;

const ContentTile = styled(Tile)`
  height: 263px;
  padding: 0;
  grid-gap: 0;
  overflow: visible;
`;

const CardBody = styled.div`
  min-height: 198px;
  padding: 18px 18px 20px;
  display: flex;
  flex-direction: column;
`;

const CardMetaBar = styled.div`
  height: 64px;
  border-top: 1px solid ${({ theme }) => theme.colors.stroke};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 0 18px;
`;

const CardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: auto;
`;

const CardHead = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
`;

const CardTitle = styled(Poppins).attrs({ px: 18, weight: 600 })`
  display: block;
  min-width: 0;
  line-height: 24px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CountWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  svg {
    width: 11px;
    height: 13px;
    fill: ${({ theme }) => theme.colors.cflowerBlue};
    opacity: 0.7;
  }
`;

const BadgeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Badge = styled.span<{ $variant?: 'draft' | 'partner' | 'published' }>`
  height: 24px;
  min-width: ${({ $variant }) => ($variant === 'draft' ? 64 : $variant === 'published' ? 108 : 74)}px;
  padding: 0 10px;
  border-radius: 3px;
  border: 1px solid
    ${({ theme, $variant }) =>
      $variant === 'partner' ? '#8CCBFF' : $variant === 'published' ? '#77DDC4' : theme.colors.stroke};
  background: ${({ $variant }) =>
    $variant === 'partner' ? '#F2FAFF' : $variant === 'published' ? '#F1FFFB' : '#F7F8FC'};
  color: ${({ theme, $variant }) =>
    $variant === 'partner' ? '#237AB8' : $variant === 'published' ? '#00A886' : theme.colors.cflowerBlue};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  line-height: 16px;
  font-weight: 600;
  text-transform: uppercase;
`;

const DetailStatusBadge = styled(Badge)`
  height: 29px;
  min-width: ${({ $variant }) => ($variant === 'published' ? 104 : 74)}px;
  font-size: 14px;
  line-height: 18px;
`;

const AuthorLogo = styled.img`
  width: 68px;
  height: 24px;
  object-fit: contain;
`;

const OwnerLogo = styled.img`
  max-width: 82px;
  height: 24px;
  object-fit: contain;
`;

const OwnerName = styled.span`
  max-width: 170px;
  height: 24px;
  padding: 0 10px;
  border: 1px solid #8ccbff;
  border-radius: 3px;
  background: #f2faff;
  color: #237ab8;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  line-height: 16px;
  font-weight: 600;
  text-transform: uppercase;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const MetaLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 90px;
`;

const MetaRight = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  color: ${({ theme }) => theme.colors.cflowerBlue};
  min-width: 0;
  cursor: pointer;

  svg {
    width: 16px;
    height: 16px;
  }
`;

const PublishButton = styled(Button)`
  width: 90px;
  min-width: 90px;
  height: 31px;
  padding: 0 12px;
  font-size: 12px;
  line-height: 16px;
`;

const Placeholder = styled.div`
  border: 1px dashed ${({ theme }) => theme.colors.stroke};
  border-radius: 6px;
  padding: 36px;
  background: ${({ theme }) => theme.colors.white};
`;

const DetailCard = styled.div`
  min-height: 390px;
  box-sizing: border-box;
  border: 1px solid ${({ theme }) => theme.colors.stroke};
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.white};
  padding: 34px 26px 40px;
`;

const DetailTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
`;

const BackRow = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 20px;
  border: 0;
  background: transparent;
  padding: 0;
  cursor: pointer;
  font-family: inherit;

  svg {
    display: block;
    transform: none;
  }

  ${({ theme }) => theme.breakpoints.down('md')} {
    gap: 10px;

    svg {
      transform: none;
      width: 14px;
      height: 14px;
    }
  }
`;

const DetailActions = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 24px;
  padding-top: 1px;

  /* Keep the shared app ellipsis hit-area from pushing visual alignment down. */
  [data-cy='tdm'] {
    margin-top: 7px;
  }
`;

const DetailPrimary = styled(Button)`
  width: 113px;
  min-width: 113px;
  height: 44px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const DetailSecondary = styled(Button)`
  width: 82px;
  min-width: 82px;
  height: 44px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const DetailMeta = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(120px, 1fr));
  gap: 42px;
  max-width: 780px;
  margin-top: 52px;

  ${({ theme }) => theme.breakpoints.down('md')} {
    grid-template-columns: repeat(2, minmax(120px, 1fr));
  }
`;

const DetailTable = styled.div`
  width: 100%;
  box-sizing: border-box;
  border: 1px solid ${({ theme }) => theme.colors.stroke};
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.white};
  overflow: hidden;
`;

const SearchWrap = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.colors.stroke};
`;

const TableRow = styled.div<{ $header?: boolean }>`
  min-height: ${({ $header }) => ($header ? 67 : 79)}px;
  display: grid;
  grid-template-columns: 18px 1.6fr 1.05fr 1fr 0.9fr 1fr 48px;
  align-items: center;
  gap: 24px;
  padding: 0 20px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.stroke};
  font-weight: ${({ $header }) => ($header ? 600 : 500)};
  cursor: ${({ $header }) => ($header ? 'default' : 'pointer')};

  &:last-child {
    border-bottom: 0;
  }

`;

const ExpandedTableContent = styled.div`
  padding: 0 20px 20px 62px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.stroke};
`;

const SortIcon = styled.span`
  display: inline-flex;
  flex-direction: column;
  gap: 2px;
  color: ${({ theme }) => theme.colors.cflowerBlue};
  margin-left: 6px;
  vertical-align: middle;

  &:before,
  &:after {
    content: '';
    width: 0;
    height: 0;
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
  }

  &:before {
    border-bottom: 6px solid currentColor;
  }

  &:after {
    border-top: 6px solid currentColor;
  }
`;

const DeleteRiskButton = styled.button`
  border: 0;
  background: transparent;
  color: ${({ theme }) => theme.colors.statusRed};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 6px;
  justify-self: end;
  padding: 0;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: ${({ theme }) => theme.colors.cultured};
  }

  svg {
    display: block;
  }
`;

const StyledModal = styled(Modal)`
  width: 566px;
  padding: 0 !important;
`;

const ModalContent = styled.div`
  padding: 26px 24px 24px;
`;

const TemplateHint = styled.div`
  border-radius: 7px;
  background: ${({ theme }) => theme.colors.cultured};
  padding: 12px 16px;

  a {
    color: ${({ theme }) => theme.colors.error};
    text-decoration: none;
    font-weight: 500;
  }
`;

const FormInput = styled(Input)`
  &[type='text'] {
    height: 50px;
    padding: 0 16px;
    background: ${({ theme }) => theme.colors.cultured};
  }
`;

const FormTextarea = styled(Textarea)`
  min-height: 100px;
  padding: 14px 16px;
  background: ${({ theme }) => theme.colors.cultured};
`;

const AuthorSelect = styled(CreatableSelect)`
  width: 100%;
  min-width: 0;
`;

const UploadBox = styled.div<{ $dragActive?: boolean }>`
  border: 1px solid ${({ theme }) => theme.colors.stroke};
  border-radius: 3px;
  background: ${({ theme, $dragActive }) => ($dragActive ? theme.colors.stroke : theme.colors.cultured)};
  min-height: 110px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 16px;
  cursor: pointer;
  transition: background 120ms ease-in-out;
`;

const UploadIcon = styled.div`
  margin-bottom: 8px;

  svg {
    width: 32px;
    height: 32px;
    display: block;
    margin: 0 auto;
  }
`;

const UploadCsvIcon = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g opacity="0.5">
      <path
        d="M22.666 12.0027C25.566 12.0187 27.1367 12.148 28.1607 13.172C29.3327 14.344 29.3327 16.2294 29.3327 20V21.3334C29.3327 25.1054 29.3327 26.9907 28.1607 28.1627C26.99 29.3334 25.1033 29.3334 21.3327 29.3334H10.666C6.89535 29.3334 5.00868 29.3334 3.83802 28.1627C2.66602 26.9894 2.66602 25.1054 2.66602 21.3334V20C2.66602 16.2294 2.66602 14.344 3.83802 13.172C4.86202 12.148 6.43268 12.0187 9.33268 12.0027"
        stroke="#6A7590"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <path
        d="M16 20.0001V2.66675M12 7.33341L16 2.66675L20 7.33341"
        stroke="#6A7590"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </g>
  </svg>
);

const SummaryCard = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.stroke};
  border-radius: 3px;
  background: ${({ theme }) => theme.colors.cultured};
  padding: 16px 18px;
`;

const FullWidthPrimary = styled(Button)`
  width: 100%;
  height: 50px;
`;

const PublishModal = styled(Modal)`
  width: 566px;
  padding: 0 !important;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  max-height: calc(100vh - 72px);
`;

const PublishHeader = styled.div`
  height: 74px;
  display: flex;
  align-items: center;
  padding: 0 24px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.stroke};
`;

const PublishContent = styled.div`
  padding: 22px 24px 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

const AccessList = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.stroke};
  border-radius: 4px;
  max-height: min(580px, calc(100vh - 510px));
  min-height: 260px;
  overflow-y: auto;
  background: ${({ theme }) => theme.colors.white};
`;

const AccessRow = styled.div<{ $indent?: number }>`
  min-height: 70px;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 16px 0 ${({ $indent }) => 16 + ($indent || 0) * 32}px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.stroke};

  &:last-child {
    border-bottom: 0;
  }

  > [data-cy='checkbox'] {
    border-color: ${({ theme }) => theme.colors.gp1};
  }

  > [data-cy='checkbox']:has(svg) {
    background: ${({ theme }) => theme.colors.gp1};
  }
`;

const WorkspaceText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;

  span {
    display: block;
    line-height: 18px;
  }
`;

const ExpandButton = styled.button`
  width: 16px;
  height: 24px;
  flex: 0 0 16px;
  border: 0;
  background: transparent;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.prussianBlue};
  padding: 0;
  font-size: 15px;
  line-height: 24px;
`;

const PlanBadge = styled.span`
  margin-left: auto;
  border: 1px solid ${({ theme }) => theme.colors.stroke};
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.cultured};
  color: ${({ theme }) => theme.colors.cflowerBlue};
  padding: 6px 10px;
  font-size: 14px;
  font-weight: 600;
`;

const PublishFooter = styled.div`
  padding: 24px;
  background: ${({ theme }) => theme.colors.white};
  flex: 0 0 auto;
`;

const ModalSelectWrap = styled.div`
  position: relative;
  z-index: 2;
`;

const CYDEA_OWNER_VALUE = '__cydea__';

type PackOwnerOption = {
  value: string;
  label: string;
  organisation?: ContentPackOwnerOrganisation | null;
};

const getPackStatus = (featureKey?: string | null): PackStatus => (featureKey ? 'published' : 'draft');

type PackBranding = {
  label?: string | null;
  logoUrl?: string | null;
  isCydea: boolean;
};

const getPackBranding = (
  pack?: ScenarioGroupSummary | ControlFrameworkSummary | ContentManagementScenarioPackSummary | ContentManagementControlFrameworkSummary | null,
): PackBranding => {
  const owner = (pack as ContentManagementScenarioPackSummary | ContentManagementControlFrameworkSummary | null | undefined)?.organisation;

  if (owner?.logoUrl) {
    return { label: owner.name, logoUrl: owner.logoUrl, isCydea: false };
  }

  if (owner?.name) {
    return { label: owner.name, isCydea: false };
  }

  const author = pack?.author?.trim();
  if (author && author.toLowerCase() !== 'cydea') {
    return { label: author, isCydea: false };
  }

  return { isCydea: true };
};

const PackBrandMark: React.FC<{ branding: PackBranding }> = ({ branding }) => {
  if (branding.logoUrl) {
    return <OwnerLogo src={branding.logoUrl} alt={branding.label || 'Author'} />;
  }

  if (branding.label) {
    return <OwnerName title={branding.label}>{branding.label}</OwnerName>;
  }

  return <AuthorLogo src={cydeaLogo} alt="Cydea" />;
};

const packOwnerOptionFromOrg = (org: PublishTargetOrgUnit): PackOwnerOption => ({
  value: org.id,
  label: org.name,
  organisation: {
    id: org.id,
    name: org.name,
    logoUrl: org.logoUrl || null,
    organisationType: org.organisationType || null,
    parentId: org.parentId || null,
  },
});

const getWorkspaceTypeTag = (workspace: PublishTargetOrgUnit) => {
  const plan = workspace.plan?.trim();
  if (plan) return plan;

  return getPlanTypeLabel(workspace.planType).replace(/\s+Plan$/, '');
};

const displayValue = (value: unknown, fallback = '-') => {
  if (value === null || value === undefined || value === '') return fallback;
  return String(value);
};

const matchesSearch = (search: string, values: unknown[]) => {
  const normalisedSearch = search.trim().toLowerCase();
  if (!normalisedSearch) return true;

  return values.some((value) => displayValue(value, '').toLowerCase().includes(normalisedSearch));
};

const toScenarioLibraryItem = (
  scenario: ScenarioPackWithDetails['scenarios'][number],
  group: ScenarioPackWithDetails,
): ScenarioLibraryItem => {
  const frequencyTimes =
    scenario.baseRisk?.frequencyTimes ??
    scenario.projectedRisk?.frequencyTimes ??
    scenario.liveRisk?.frequencyTimes ??
    null;

  const frequencyYears =
    scenario.baseRisk?.frequencyYears ??
    scenario.projectedRisk?.frequencyYears ??
    scenario.liveRisk?.frequencyYears ??
    null;

  const expectedLoss =
    scenario.baseRisk?.expectedLoss ?? scenario.projectedRisk?.expectedLoss ?? scenario.liveRisk?.expectedLoss ?? null;

  return {
    id: scenario.id,
    name: scenario.name,
    author: group.author ?? scenario.ownerName ?? undefined,
    groupName: group.name,
    description:
      scenario.description ||
      scenario.managementNote ||
      scenario.riskNarrative ||
      scenario.consequenceNote ||
      undefined,
    source: scenario.source,
    event: scenario.event,
    consequence: scenario.consequence,
    riskNarrative: scenario.riskNarrative,
    frequencyTimes,
    frequencyYears,
    frequencyNote: scenario.frequencyNote ?? undefined,
    expectedLoss: expectedLoss ?? undefined,
    managementStrategy: scenario.managementStrategy ?? undefined,
    lowerFormula: scenario.lowerFormula ?? undefined,
    upperFormula: scenario.upperFormula ?? undefined,
    note: scenario.consequenceNote ?? scenario.managementNote ?? undefined,
    assessmentId: scenario.assessment?.id ?? undefined,
  };
};

const ContentOverflowMenu: React.FC<{
  items: Array<{ type: CallbackType; label: string; onClick: () => void }>;
  onClickAwayClose?: () => void;
}> = ({ items, onClickAwayClose }) => {
  const [anchorEl, setAnchorEl] = useState<React.MouseEvent['currentTarget'] | null>(null);

  return (
    <>
      <CommonMenu
        configuredList={items.map((item) => ({
          type: item.type,
          label: item.label,
          exe: (e) => {
            e.stopPropagation();
            setAnchorEl(null);
            item.onClick();
          },
        }))}
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={(event, reason) => {
          (event as React.SyntheticEvent | undefined)?.stopPropagation?.();
          setAnchorEl(null);
          if (reason === 'backdropClick') {
            onClickAwayClose?.();
          }
        }}
        top={0}
      />
      <ThreeDots
        onClick={(e) => {
          e.stopPropagation();
          setAnchorEl(e.currentTarget);
        }}
      />
    </>
  );
};

interface ContentCardProps {
  title: string;
  description?: string | null;
  countLabel: string;
  branding: PackBranding;
  versionNumber?: number | null;
  updatedAt?: string | null;
  isDraft?: boolean;
  onView: () => void;
  onEdit: () => void;
  onPublish: () => void;
  onHide: () => void;
  onVersionHistory: () => void;
  onDelete: () => void;
}

const ContentCard: React.FC<ContentCardProps> = ({
  title,
  description,
  branding,
  countLabel,
  versionNumber,
  updatedAt,
  isDraft,
  onView,
  onEdit,
  onPublish,
  onHide,
  onVersionHistory,
  onDelete,
}) => {
  const suppressNextCardClickRef = useRef(false);

  return (
    <ContentTile
      className="hover-shadow"
      data-cy="content-management-card"
      onClick={() => {
        if (suppressNextCardClickRef.current) {
          suppressNextCardClickRef.current = false;
          return;
        }

        onView();
      }}
    >
      <CardBody>
        <CardHead>
          <CardTitle>{title}</CardTitle>
          <ContentOverflowMenu
            items={[
              { type: 'VIEW', label: 'View', onClick: onView },
              { type: 'EDIT', label: 'Edit details', onClick: onEdit },
              { type: 'VERSION_HISTORY', label: 'Version history', onClick: onVersionHistory },
              { type: 'HIDE_CONTENT', label: 'Hide content', onClick: onHide },
              { type: 'DELETE', label: 'Delete', onClick: onDelete },
            ]}
            onClickAwayClose={() => {
              suppressNextCardClickRef.current = true;
            }}
          />
        </CardHead>
        <Spacer $px={32} />
        <TileDescriptionContainer>
          <TileDescription>
            {description && (
              <Poppins px={14} color="cflowerBlue">
                {description}
              </Poppins>
            )}
          </TileDescription>
        </TileDescriptionContainer>
        <CardFooter>
          <CountWrap>
            <ScenarioSvg />
            <Poppins px={14} color="cflowerBlue">
              {countLabel}
            </Poppins>
          </CountWrap>
          <BadgeRow>
            {isDraft && <Badge $variant="draft">DRAFT</Badge>}
            <PackBrandMark branding={branding} />
          </BadgeRow>
        </CardFooter>
      </CardBody>
      <CardMetaBar>
        <MetaLeft>
          {isDraft ? (
            <PublishButton
              primary
              onClick={(e) => {
                e.stopPropagation();
                onPublish();
              }}
            >
              Publish
            </PublishButton>
          ) : (
            <Poppins px={14} color="cflowerBlue">
              v{versionNumber || 1}
            </Poppins>
          )}
        </MetaLeft>
        <MetaRight
          role="button"
          tabIndex={0}
          onClick={(e) => {
            e.stopPropagation();
            onEdit();
          }}
          onKeyDown={(e) => {
            if (e.key !== 'Enter' && e.key !== ' ') return;
            e.preventDefault();
            e.stopPropagation();
            onEdit();
          }}
        >
          <ContentEditIcon />
          <Poppins px={14} color="cflowerBlue" nowrap>
            {formatDate(updatedAt, { formatType: 'datetime', emptyValue: '-' })}
          </Poppins>
        </MetaRight>
      </CardMetaBar>
    </ContentTile>
  );
};

interface ScenarioPackDraftModalProps {
  mode: 'add' | 'edit';
  isOpen: boolean;
  group?: ScenarioPackWithDetails | null;
  onClose: () => void;
  onSaved: () => Promise<void> | void;
}

const ScenarioPackDraftModal: React.FC<ScenarioPackDraftModalProps> = ({ mode, isOpen, group, onClose, onSaved }) => {
  const { variables: workspaceVariables, isLoading: isVariablesLoading } = useWorkspaceVariables();
  const [file, setFile] = useState<File | null>(null);
  const [packName, setPackName] = useState('');
  const [description, setDescription] = useState('');
  const [author, setAuthor] = useState('');
  const [ownerOrganisationId, setOwnerOrganisationId] = useState<string | null>(null);
  const [scenarioPayload, setScenarioPayload] = useState<ScenarioPackImportPayload | null>(null);
  const [validationErrors, setValidationErrors] = useState<ValidationErrorItem[]>([]);
  const [createdCount, setCreatedCount] = useState(0);
  const [updatedCount, setUpdatedCount] = useState(0);
  const [error, setError] = useState('');
  const [isParsing, setIsParsing] = useState(false);
  const templateLink = useMemo(() => generateScenarioPackTemplateLink(), []);
  const ownerQuery = useFlatOrganisationsHomeQuery({
    variables: { includeArchived: false },
    skip: !isOpen,
  });

  const [runCreateScenarioGroup, createState] = useMutation<
    CreateScenarioGroupFromImportMutation,
    CreateScenarioGroupFromImportMutationVariables
  >(CREATE_SCENARIO_GROUP_FROM_IMPORT);
  const [runImportScenarioGroup, importState] = useMutation<ImportScenarioGroupMutation, ImportScenarioGroupMutationVariables>(
    IMPORT_SCENARIO_GROUP,
  );
  const [runUpdateMetadata, metadataState] = useMutation<
    UpdateScenarioGroupMetadataMutation,
    UpdateScenarioGroupMetadataMutationVariables
  >(UPDATE_SCENARIO_GROUP_METADATA);

  const isSubmitting = createState.loading || importState.loading || metadataState.loading;

  useEffect(() => {
    return () => URL.revokeObjectURL(templateLink);
  }, [templateLink]);

  useEffect(() => {
    if (!isOpen) return;
    setFile(null);
    setPackName(group?.name || '');
    setDescription(group?.description || '');
    setAuthor(group?.author || group?.organisation?.name || 'Cydea');
    setOwnerOrganisationId(group?.organisation?.id || null);
    setScenarioPayload(null);
    setValidationErrors([]);
    setCreatedCount(0);
    setUpdatedCount(0);
    setError('');
    setIsParsing(false);
  }, [isOpen, group]);

  const onDrop = async (acceptedFiles: File[]) => {
    const selectedFile = acceptedFiles[0];
    if (!selectedFile) return;

    if (isVariablesLoading) {
      setError('Workspace variables are still loading. Please try again in a moment.');
      return;
    }

    setFile(selectedFile);
    setError('');
    setIsParsing(true);
    setScenarioPayload(null);
    setValidationErrors([]);
    setCreatedCount(0);
    setUpdatedCount(0);

    try {
      const parsed = await parseScenarioPackCsv({
        file: selectedFile,
        variables: workspaceVariables,
        existingScenarioIds: group?.scenarios?.map((scenario) => scenario.id) || [],
        requirePackName: false,
      });

      setScenarioPayload(parsed.payload);
      setValidationErrors(parsed.validationErrors);
      setCreatedCount(parsed.createdCount);
      setUpdatedCount(parsed.updatedCount);

      if (parsed.payload?.group?.name && !packName.trim()) setPackName(parsed.payload.group.name);
      if (parsed.payload?.group?.description && !description.trim()) setDescription(parsed.payload.group.description);
      if (parsed.payload?.group?.author && !author.trim()) setAuthor(parsed.payload.group.author);
    } finally {
      setIsParsing(false);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    accept: { 'text/csv': ['.csv'] },
    disabled: isVariablesLoading || isSubmitting,
  });

  const hasValidatedFile = !!scenarioPayload && !validationErrors.length && !isVariablesLoading && !isParsing;
  const canSave = !!packName.trim() && !isSubmitting && (mode === 'edit' || hasValidatedFile);
  const ownerOptions = useMemo<PackOwnerOption[]>(() => {
    const partners = ((ownerQuery.data?.flatOrganisations || []) as PublishTargetOrgUnit[])
      .filter((org) => org.recordType === 'O' && !org.isArchived && org.organisationType === 'PARTNER')
      .map(packOwnerOptionFromOrg)
      .sort((a, b) => a.label.localeCompare(b.label));

    return [{ value: CYDEA_OWNER_VALUE, label: 'Cydea', organisation: null }, ...partners];
  }, [ownerQuery.data]);
  const selectedAuthor =
    (ownerOrganisationId
      ? ownerOptions.find((option) => option.value === ownerOrganisationId) ||
        (group?.organisation ? { value: group.organisation.id, label: group.organisation.name, organisation: group.organisation } : null)
      : author.trim() && author.trim().toLowerCase() !== 'cydea'
      ? { value: author.trim(), label: author.trim(), organisation: null }
      : null) ||
    (ownerOrganisationId && group?.organisation
      ? { value: group.organisation.id, label: group.organisation.name, organisation: group.organisation }
      : null) ||
    ownerOptions[0];
  const selectedOwnerOrganisationId = selectedAuthor?.organisation ? selectedAuthor.value : null;
  const selectedAuthorName = selectedAuthor?.label || 'Cydea';

  const handleClose = () => {
    if (isSubmitting) return;
    onClose();
  };

  const onSave = async () => {
    if (!packName.trim()) return;
    setError('');

    try {
      if (mode === 'add') {
        if (!scenarioPayload) return;
        await runCreateScenarioGroup({
          variables: {
            input: {
              ...scenarioPayload,
              group: {
                ...scenarioPayload.group,
                id: null,
                organisationId: selectedOwnerOrganisationId,
                name: packName.trim(),
                description: description.trim() || null,
                author: selectedAuthorName,
                authorLogoPath: null,
                isSystem: !selectedOwnerOrganisationId,
              },
            },
          },
        });
      } else if (group && scenarioPayload) {
        await runImportScenarioGroup({
          variables: {
            scenarioGroupId: group.id,
            input: {
              ...scenarioPayload,
              group: {
                ...scenarioPayload.group,
                id: group.id,
                organisationId: selectedOwnerOrganisationId,
                name: packName.trim(),
                description: description.trim() || null,
                author: selectedAuthorName,
                authorLogoPath: null,
                isSystem: !selectedOwnerOrganisationId,
              },
            },
          },
        });
      } else if (group) {
        await runUpdateMetadata({
          variables: {
            scenarioGroupId: group.id,
            group: {
              id: group.id,
              organisationId: selectedOwnerOrganisationId,
              name: packName.trim(),
              description: description.trim() || null,
              author: selectedAuthorName,
              authorLogoPath: null,
              category: group.category || null,
              isSystem: !selectedOwnerOrganisationId,
            },
          },
        });
      }

      await onSaved();
      showSuccessToast({ title: mode === 'add' ? 'Scenario pack saved as draft' : 'Scenario pack updated' });
      handleClose();
    } catch (err) {
      const parsedValidationErrors = parseGraphqlCsvValidationErrors(err);
      if (parsedValidationErrors.length > 0) setValidationErrors(parsedValidationErrors);
      setError(err instanceof Error ? err.message : 'Scenario pack save failed.');
    }
  };

  return (
    <StyledModal isOpen={isOpen} onRequestClose={handleClose} persistent={isSubmitting}>
      <ModalContent>
        <Poppins className="m-title" px={28}>
          {mode === 'add' ? 'Add scenario pack' : 'Edit scenario pack'}
        </Poppins>
        <Spacer $px={18} />
        <TemplateHint>
          <Poppins px={14}>
            {mode === 'add' ? (
              <>
                <a href={templateLink} download="scenario_pack_template.csv">
                  Click here
                </a>{' '}
                to download the CSV template for scenario pack
              </>
            ) : (
              <>
                <a
                  href="#download-current-scenario-pack"
                  onClick={(event) => {
                    event.preventDefault();
                    if (!group || isVariablesLoading) return;
                    downloadScenarioGroupCsv({ group, workspaceVariables });
                  }}
                >
                  Click here
                </a>{' '}
                to download and edit the current content of this pack in CSV.
              </>
            )}
          </Poppins>
        </TemplateHint>

        <Spacer $px={26} />
        <InputLabel>NAME</InputLabel>
        <Spacer $px={8} />
        <FormInput value={packName} onChange={(e) => setPackName(e.target.value)} placeholder="Name of scenario pack" maxLength={255} />

        <Spacer $px={18} />
        <InputLabel>DESCRIPTION</InputLabel>
        <Spacer $px={8} />
        <FormTextarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Description of scenario pack"
          maxLength={2048}
        />

        <Spacer $px={18} />
        <InputLabel>AUTHOR</InputLabel>
        <Spacer $px={8} />
        <ModalSelectWrap>
          <AuthorSelect
            options={ownerOptions}
            value={selectedAuthor}
            onChange={(nextValue: unknown) => {
              const nextOwner = nextValue as PackOwnerOption | null;
              setOwnerOrganisationId(nextOwner?.organisation ? nextOwner.value : null);
              setAuthor(nextOwner?.label || 'Cydea');
            }}
            placeholder="Type in or select author"
            isDisabled={isSubmitting || ownerQuery.loading}
            isSearchable
            formatCreateLabel={(value: string) => value}
          />
        </ModalSelectWrap>

        <Spacer $px={18} />
        <InputLabel>{mode === 'add' ? 'UPLOAD A CSV FILE' : 'UPLOAD UPDATED CSV FILE'}</InputLabel>
        <Spacer $px={8} />
        <UploadBox {...getRootProps()} $dragActive={isDragActive}>
          <input {...getInputProps()} />
          <div>
            {!file && (
              <UploadIcon>
                <UploadCsvIcon />
              </UploadIcon>
            )}
            {file ? (
              <Poppins px={16} weight={500}>
                File selected: {file.name}
              </Poppins>
            ) : (
              <Poppins px={16} weight={500} color="cflowerBlue">
                {isDragActive ? 'Drop the CSV file here' : 'Click to upload CSV file'}
              </Poppins>
            )}
          </div>
        </UploadBox>

        {isParsing && (
          <>
            <Spacer $px={10} />
            <Poppins px={14}>Validating CSV file...</Poppins>
          </>
        )}

        <Spacer $px={16} />
        <CsvValidationErrors errors={validationErrors} />

        {(hasValidatedFile || mode === 'edit') && file && (
          <SummaryCard>
            <SCVSummary
              items={[
                hasValidatedFile ? 'File validated' : '',
                `${createdCount + updatedCount} scenarios ${mode === 'edit' ? 'ready to be saved' : 'added'}`,
              ].filter(Boolean)}
            />
          </SummaryCard>
        )}

        {!!error && (
          <>
            <Spacer $px={14} />
            <Poppins px={14} color="error">
              {error}
            </Poppins>
          </>
        )}

        <Spacer $px={30} />
        <FullWidthPrimary primary onClick={onSave} disabled={!canSave} loading={isSubmitting}>
          Save as draft
        </FullWidthPrimary>
      </ModalContent>
    </StyledModal>
  );
};

interface PublishContentAccessModalProps {
  isOpen: boolean;
  type: 'scenario' | 'framework';
  item: ScenarioGroupSummary | ControlFrameworkSummary | null;
  onClose: () => void;
  onPublished: () => Promise<void> | void;
}

const PublishContentAccessModal: React.FC<PublishContentAccessModalProps> = ({ isOpen, type, item, onClose, onPublished }) => {
  const { flatOrganisations } = useAuth();
  const targetQuery = useFlatOrganisationsHomeQuery({
    variables: { includeArchived: false },
    skip: !isOpen,
  });
  const [selectedOrganisationIds, setSelectedOrganisationIds] = useState<string[]>([]);
  const [selectedWorkspaceIds, setSelectedWorkspaceIds] = useState<string[]>([]);
  const [expandedIds, setExpandedIds] = useState<string[]>([]);
  const [error, setError] = useState('');
  const featureName = contentFeatureKey(type, item);
  const featureQuery = useQuery<ContentFeaturesQuery>(CONTENT_FEATURES, {
    skip: !isOpen || !featureName,
    fetchPolicy: 'network-only',
  });

  const [runUpsertScenarioFeature, scenarioState] = useMutation<
    UpsertScenarioPackFeatureMutation,
    UpsertScenarioPackFeatureMutationVariables
  >(UPSERT_SCENARIO_PACK_FEATURE);
  const [runUpsertFrameworkFeature, frameworkState] = useMutation<
    UpsertControlFrameworkFeatureMutation,
    UpsertControlFrameworkFeatureMutationVariables
  >(UPSERT_CONTROL_FRAMEWORK_FEATURE);

  const isPublishing = scenarioState.loading || frameworkState.loading;
  const isLoadingTargets = featureQuery.loading || targetQuery.loading;
  const isScenario = type === 'scenario';
  const scenarioItem = isScenario ? (item as ScenarioGroupSummary | null) : null;
  const frameworkItem = !isScenario ? (item as ControlFrameworkSummary | null) : null;
  const itemName = scenarioItem?.name || frameworkItem?.shortname || frameworkItem?.name || '';
  const count = scenarioItem?.scenarios?.length ?? frameworkItem?.controlCount ?? 0;
  const branding = getPackBranding(item as ContentManagementScenarioPackSummary | ContentManagementControlFrameworkSummary | null);
  const targetOrgUnits = useMemo<PublishTargetOrgUnit[]>(() => {
    const source = targetQuery.data?.flatOrganisations?.length ? targetQuery.data.flatOrganisations : flatOrganisations || [];
    return source as PublishTargetOrgUnit[];
  }, [flatOrganisations, targetQuery.data]);

  const organisations = useMemo(() => {
    return targetOrgUnits.filter((org) => org.recordType === 'O' && !org.isArchived);
  }, [targetOrgUnits]);

  const workspacesByParent = useMemo(() => {
    const map = new Map<string, PublishTargetOrgUnit[]>();
    targetOrgUnits
      .filter((org) => org.recordType === 'W' && !org.isArchived)
      .forEach((workspace) => {
        const parentId = workspace.parentId || '';
        map.set(parentId, [...(map.get(parentId) || []), workspace]);
      });
    return map;
  }, [targetOrgUnits]);

  useEffect(() => {
    if (!isOpen) return;
    setExpandedIds(organisations.slice(0, 3).map((org) => org.id));
    setError('');
  }, [isOpen, organisations]);

  useEffect(() => {
    if (!isOpen || !featureName) return;

    const existingFeature = featureQuery.data?.features?.find((feature) => feature.name === featureName);
    setSelectedOrganisationIds(existingFeature?.organisations || []);
    setSelectedWorkspaceIds(existingFeature?.workspaces || []);
  }, [featureName, featureQuery.data, isOpen]);

  const toggleOrganisation = (organisationId: string) => {
    const childWorkspaceIds = (workspacesByParent.get(organisationId) || []).map((workspace) => workspace.id);

    setSelectedOrganisationIds((prev) => {
      const isSelected = prev.includes(organisationId);

      setSelectedWorkspaceIds((currentWorkspaceIds) =>
        isSelected
          ? currentWorkspaceIds.filter((workspaceId) => !childWorkspaceIds.includes(workspaceId))
          : Array.from(new Set([...currentWorkspaceIds, ...childWorkspaceIds])),
      );

      return isSelected ? prev.filter((id) => id !== organisationId) : [...prev, organisationId];
    });
  };

  const toggleWorkspace = (workspaceId: string) => {
    setSelectedWorkspaceIds((prev) =>
      prev.includes(workspaceId) ? prev.filter((id) => id !== workspaceId) : [...prev, workspaceId],
    );
  };

  const hasTargets = selectedOrganisationIds.length > 0 || selectedWorkspaceIds.length > 0;

  const onPublish = async () => {
    if (!item || !hasTargets) return;
    setError('');

    try {
      if (isScenario && scenarioItem) {
        await runUpsertScenarioFeature({
          variables: {
            name: featureName,
            general: false,
            organisations: selectedOrganisationIds,
            workspaces: selectedWorkspaceIds,
          },
        });
      } else if (frameworkItem) {
        await runUpsertFrameworkFeature({
          variables: {
            name: featureName,
            general: false,
            organisations: selectedOrganisationIds,
            workspaces: selectedWorkspaceIds,
          },
        });
      }

      await onPublished();
      showSuccessToast({ title: `${isScenario ? 'Scenario pack' : 'Framework pack'} published` });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Publish failed.');
    }
  };

  return (
    <PublishModal isOpen={isOpen} onRequestClose={onClose} persistent={isPublishing}>
      <PublishHeader>
        <Poppins px={18} weight={600} color="cflowerBlue">
          Publish {isScenario ? 'scenario' : 'framework'} pack
        </Poppins>
      </PublishHeader>
      <PublishContent>
        <Poppins px={22} weight={600}>
          {itemName}
        </Poppins>
        <Spacer $px={16} />
        <BadgeRow>
          <PackBrandMark branding={branding} />
          <CountWrap>
            <ScenarioSvg />
            <Poppins px={14} color="cflowerBlue">
              {count} {isScenario ? 'Scenarios' : 'Controls'}
            </Poppins>
          </CountWrap>
          <Poppins px={14} color="cflowerBlue">
            Version {scenarioItem?.versionNumber || frameworkItem?.versionNumber || 1}.0
          </Poppins>
        </BadgeRow>
        <Spacer $px={30} />
        <Poppins px={14} weight={600}>
          Select organisations or workspaces that can see this pack
        </Poppins>
        <Spacer $px={10} />
        <Poppins px={14} color="cflowerBlue" css="max-width: 430px; display: block; line-height: 24px;">
          New workspaces created under organisations you have selected will have access to this content by default
        </Poppins>
        <Spacer $px={30} />
        <AccessList>
          <AccessRow>
            <AppCheckBox
              isChecked={selectedOrganisationIds.length === organisations.length && organisations.length > 0}
              onClick={() => {
                const allOrgIds = organisations.map((org) => org.id);
                const isSelectingAll = selectedOrganisationIds.length !== allOrgIds.length;
                setSelectedOrganisationIds(isSelectingAll ? allOrgIds : []);
                setSelectedWorkspaceIds(
                  isSelectingAll
                    ? Array.from(new Set(Array.from(workspacesByParent.values()).flat().map((workspace) => workspace.id)))
                    : [],
                );
              }}
            />
            <Poppins px={14}>All Organisations</Poppins>
          </AccessRow>
          {organisations.map((org) => {
            const workspaces = workspacesByParent.get(org.id) || [];
            const expanded = expandedIds.includes(org.id);
            return (
              <React.Fragment key={org.id}>
                <AccessRow>
                  <ExpandButton
                    type="button"
                    aria-label="Toggle organisation"
                    onClick={() =>
                      setExpandedIds((prev) =>
                        prev.includes(org.id) ? prev.filter((id) => id !== org.id) : [...prev, org.id],
                      )
                    }
                  >
                    {expanded ? '▾' : '▸'}
                  </ExpandButton>
                  <AppCheckBox isChecked={selectedOrganisationIds.includes(org.id)} onClick={() => toggleOrganisation(org.id)} />
                  <Poppins px={14} weight={600}>
                    {org.name}
                  </Poppins>
                  <Poppins px={14} color="cflowerBlue" css="margin-left: auto;">
                    {workspaces.length} workspaces
                  </Poppins>
                </AccessRow>
                {expanded &&
                  workspaces.map((workspace) => {
                    const workspaceTypeTag = getWorkspaceTypeTag(workspace);

                    return (
                      <AccessRow key={workspace.id} $indent={2}>
                        <AppCheckBox
                          isChecked={selectedWorkspaceIds.includes(workspace.id)}
                          onClick={() => toggleWorkspace(workspace.id)}
                        />
                        <WorkspaceText>
                          <Poppins px={14}>{workspace.name}</Poppins>
                          <Poppins px={14} color="cflowerBlue">
                            Workspace
                          </Poppins>
                        </WorkspaceText>
                        {workspaceTypeTag && <PlanBadge>{workspaceTypeTag}</PlanBadge>}
                      </AccessRow>
                    );
                  })}
              </React.Fragment>
            );
          })}
        </AccessList>
        {!!error && (
          <>
            <Spacer $px={12} />
            <Poppins px={14} color="error">
              {error}
            </Poppins>
          </>
        )}
      </PublishContent>
      <PublishFooter>
        <FullWidthPrimary primary onClick={onPublish} disabled={!item || !hasTargets || isPublishing || isLoadingTargets} loading={isPublishing}>
          Publish now
        </FullWidthPrimary>
      </PublishFooter>
    </PublishModal>
  );
};

const ScenarioDetailView: React.FC<{
  group: ScenarioPackWithDetails;
  onBack: () => void;
  onEdit: () => void;
  onPublish: () => void;
  onVersionHistory: () => void;
  onManageAccess: () => void;
  onDeletePack: () => void;
  onDeleteScenario: (scenarioId: string) => void;
}> = ({ group, onBack, onEdit, onPublish, onVersionHistory, onManageAccess, onDeletePack, onDeleteScenario }) => {
  const theme = useTheme();
  const status = getPackStatus(group.featureKey);
  const branding = getPackBranding(group);
  const [expandedScenarioIds, setExpandedScenarioIds] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const filteredScenarios = (group.scenarios || []).filter((scenario) =>
    matchesSearch(search, [
      scenario.name,
      getRiskSourcesName(scenario.source as any),
      getRiskEventsName(scenario.event as any),
      getRiskConsequencesName(scenario.consequence as any),
      group.category,
      scenario.description,
      scenario.riskNarrative,
      scenario.managementNote,
      scenario.consequenceNote,
      scenario.ownerName,
    ]),
  );

  const toggleExpandedScenario = (scenarioId: string) => {
    setExpandedScenarioIds((prev) =>
      prev.includes(scenarioId) ? prev.filter((id) => id !== scenarioId) : [...prev, scenarioId],
    );
  };

  return (
    <Div>
      <DetailCard>
        <DetailTop>
          <div>
            <BackRow type="button" onClick={onBack}>
              <BackSvg fill={theme.colors.cflowerBlue} />
              <GradientText>Scenario Packs</GradientText>
            </BackRow>
            <Spacer $px={36} />
            <Poppins px={32} weight={600}>
              {group.name}
            </Poppins>
          </div>
          <DetailActions>
            <DetailPrimary primary onClick={onPublish}>
              Publish
            </DetailPrimary>
            <DetailSecondary secondary onClick={onEdit}>
              Edit
            </DetailSecondary>
            <ContentOverflowMenu
              items={[
                { type: 'VERSION_HISTORY', label: 'Version history', onClick: onVersionHistory },
                { type: 'GRANT_ACCESS', label: 'Manage access', onClick: onManageAccess },
                { type: 'DELETE', label: 'Delete', onClick: onDeletePack },
              ]}
            />
          </DetailActions>
        </DetailTop>
        <DetailMeta>
          <div>
            <InputLabel>AUTHOR</InputLabel>
            <Spacer $px={12} />
            <PackBrandMark branding={branding} />
          </div>
          <div>
            <InputLabel>LAST EDITED</InputLabel>
            <Spacer $px={12} />
            <Poppins px={14} weight={500}>
              {formatDate(group.meta?.updatedAt || group.meta?.createdAt, { formatType: 'datetime', emptyValue: '-' })}
            </Poppins>
          </div>
          <div>
            <InputLabel>VERSION</InputLabel>
            <Spacer $px={12} />
            <Poppins px={14} weight={500}>
              {group.versionNumber || 1}.0
            </Poppins>
          </div>
          <div>
            <InputLabel>STATUS</InputLabel>
            <Spacer $px={12} />
            <DetailStatusBadge $variant={status === 'draft' ? 'draft' : 'published'}>{status}</DetailStatusBadge>
          </div>
        </DetailMeta>
        <Spacer $px={44} />
        <InputLabel>DESCRIPTION</InputLabel>
        <Spacer $px={12} />
        <Poppins px={14} weight={500}>
          {group.description || '-'}
        </Poppins>
      </DetailCard>
      <Spacer $px={26} />
      <DetailTable>
        <SearchWrap>
          <GTSearch value={search} onChange={setSearch} tableId="content-management-scenario-pack" />
        </SearchWrap>
        <TableRow $header>
          <div />
          <Poppins px={16} weight={600}>Name<SortIcon /></Poppins>
          <Poppins px={16} weight={600}>Source<SortIcon /></Poppins>
          <Poppins px={16} weight={600}>Event<SortIcon /></Poppins>
          <Poppins px={16} weight={600}>Consequence<SortIcon /></Poppins>
          <Poppins px={16} weight={600}>Group<SortIcon /></Poppins>
          <div />
        </TableRow>
        {filteredScenarios.map((scenario) => {
          const isExpanded = expandedScenarioIds.includes(scenario.id);
          const sheetData = toScenarioLibraryItem(scenario, group);

          return (
            <React.Fragment key={scenario.id}>
              <TableRow onClick={() => toggleExpandedScenario(scenario.id)} data-expanded={isExpanded}>
                <GTExpander onClick={() => toggleExpandedScenario(scenario.id)} isExpanded={isExpanded} />
                <Poppins px={14} weight={500} nowrap>{displayValue(scenario.name, 'Scenario Name...')}</Poppins>
                <Poppins px={14} nowrap>{displayValue(getRiskSourcesName(scenario.source as any))}</Poppins>
                <Poppins px={14} nowrap>{displayValue(getRiskEventsName(scenario.event as any))}</Poppins>
                <Poppins px={14} nowrap>{displayValue(getRiskConsequencesName(scenario.consequence as any))}</Poppins>
                <Poppins px={14} nowrap>{displayValue(group.category)}</Poppins>
                <DeleteRiskButton
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteScenario(scenario.id);
                  }}
                  aria-label="Delete scenario"
                >
                  <ContentDeleteIcon />
                </DeleteRiskButton>
              </TableRow>
              {isExpanded && (
                <ExpandedTableContent>
                  <TableSheetScenarioLibrary data={sheetData} />
                </ExpandedTableContent>
              )}
            </React.Fragment>
          );
        })}
      </DetailTable>
    </Div>
  );
};

const FrameworkDetailView: React.FC<{
  framework: ContentManagementControlFrameworkSummary;
  onBack: () => void;
  onEdit: () => void;
  onPublish: () => void;
  onVersionHistory: () => void;
  onManageAccess: () => void;
  onDeletePack: () => void;
  onDeleteControl: (controlId: string) => void;
}> = ({ framework, onBack, onEdit, onPublish, onVersionHistory, onManageAccess, onDeletePack, onDeleteControl }) => {
  const theme = useTheme();
  const status = getPackStatus(framework.featureKey);
  const branding = getPackBranding(framework);
  const [expandedControlIds, setExpandedControlIds] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const filteredControls = (framework.frameworkControls || []).filter((control) =>
    matchesSearch(search, [
      control.control_identifier,
      control.name,
      control.shortname,
      control.strategy,
      control.description,
      control.controlFramework?.author,
      framework.author,
      control.controlFramework?.shortname,
      framework.shortname,
      framework.name,
    ]),
  );

  const toggleExpandedControl = (controlId: string) => {
    setExpandedControlIds((prev) =>
      prev.includes(controlId) ? prev.filter((id) => id !== controlId) : [...prev, controlId],
    );
  };

  return (
    <Div>
      <DetailCard>
        <DetailTop>
          <div>
            <BackRow type="button" onClick={onBack}>
              <BackSvg fill={theme.colors.cflowerBlue} />
              <GradientText>Framework Packs</GradientText>
            </BackRow>
            <Spacer $px={36} />
            <Poppins px={32} weight={600}>
              {framework.shortname || framework.name}
            </Poppins>
          </div>
          <DetailActions>
            <DetailPrimary primary onClick={onPublish}>
              Publish
            </DetailPrimary>
            <DetailSecondary secondary onClick={onEdit}>
              Edit
            </DetailSecondary>
            <ContentOverflowMenu
              items={[
                { type: 'VERSION_HISTORY', label: 'Version history', onClick: onVersionHistory },
                { type: 'GRANT_ACCESS', label: 'Manage access', onClick: onManageAccess },
                { type: 'DELETE', label: 'Delete', onClick: onDeletePack },
              ]}
            />
          </DetailActions>
        </DetailTop>
        <DetailMeta>
          <div>
            <InputLabel>AUTHOR</InputLabel>
            <Spacer $px={12} />
            <PackBrandMark branding={branding} />
          </div>
          <div>
            <InputLabel>LAST EDITED</InputLabel>
            <Spacer $px={12} />
            <Poppins px={14} weight={500}>
              {formatDate(framework.meta?.updatedAt || framework.meta?.createdAt, { formatType: 'datetime', emptyValue: '-' })}
            </Poppins>
          </div>
          <div>
            <InputLabel>VERSION</InputLabel>
            <Spacer $px={12} />
            <Poppins px={14} weight={500}>
              {framework.versionNumber || 1}.0
            </Poppins>
          </div>
          <div>
            <InputLabel>STATUS</InputLabel>
            <Spacer $px={12} />
            <DetailStatusBadge $variant={status === 'draft' ? 'draft' : 'published'}>{status}</DetailStatusBadge>
          </div>
        </DetailMeta>
        <Spacer $px={44} />
        <InputLabel>DESCRIPTION</InputLabel>
        <Spacer $px={12} />
        <Poppins px={14} weight={500}>
          {framework.description || framework.name || '-'}
        </Poppins>
      </DetailCard>
      <Spacer $px={26} />
      <DetailTable>
        <SearchWrap>
          <GTSearch value={search} onChange={setSearch} tableId="content-management-framework-pack" />
        </SearchWrap>
        <TableRow $header>
          <div />
          <Poppins px={16} weight={600}>Framework Control ID<SortIcon /></Poppins>
          <Poppins px={16} weight={600}>Name<SortIcon /></Poppins>
          <Poppins px={16} weight={600}>Strategy<SortIcon /></Poppins>
          <Poppins px={16} weight={600}>Author<SortIcon /></Poppins>
          <Poppins px={16} weight={600}>Framework<SortIcon /></Poppins>
          <div />
        </TableRow>
        {filteredControls.map((control) => {
          const isExpanded = expandedControlIds.includes(control.id);
          const sheetData = {
            ...control,
            controlFramework: {
              shortname: framework.shortname || framework.name,
              description: framework.description,
              ...(control.controlFramework || {}),
              author: 'Cydea',
            },
          };

          return (
            <React.Fragment key={control.id}>
              <TableRow onClick={() => toggleExpandedControl(control.id)} data-expanded={isExpanded}>
                <GTExpander onClick={() => toggleExpandedControl(control.id)} isExpanded={isExpanded} />
                <Poppins px={14} weight={500} nowrap>{displayValue(control.control_identifier)}</Poppins>
                <Poppins px={14} nowrap>{displayValue(control.name, 'Control Name...')}</Poppins>
                <Poppins px={14} nowrap>{displayValue(control.strategy)}</Poppins>
                <Poppins px={14} nowrap>Cydea</Poppins>
                <Poppins px={14} nowrap>{displayValue(control.controlFramework?.shortname || framework.shortname)}</Poppins>
                <DeleteRiskButton
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteControl(control.id);
                  }}
                  aria-label="Delete control"
                >
                  <ContentDeleteIcon />
                </DeleteRiskButton>
              </TableRow>
              {isExpanded && (
                <ExpandedTableContent>
                  <TableSheetControlLibrary data={sheetData as any} />
                </ExpandedTableContent>
              )}
            </React.Fragment>
          );
        })}
      </DetailTable>
    </Div>
  );
};

const ContentManagementPage = () => {
  const canManageContent = useCanManageContent();
  const [tab, setTab] = useState<Tab>('scenarios');
  const [isAddScenarioPackOpen, setIsAddScenarioPackOpen] = useState(false);
  const [editScenarioPack, setEditScenarioPack] = useState<ScenarioPackWithDetails | null>(null);
  const [publishScenarioPack, setPublishScenarioPack] = useState<ScenarioPackWithDetails | null>(null);
  const [versionHistoryGroup, setVersionHistoryGroup] = useState<ScenarioGroupSummary | null>(null);
  const [deleteGroup, setDeleteGroup] = useState<ScenarioGroupSummary | null>(null);
  const [detailGroup, setDetailGroup] = useState<ScenarioPackWithDetails | null>(null);
  const [deleteScenario, setDeleteScenario] = useState<{ group: ScenarioPackWithDetails; scenarioId: string } | null>(null);
  const [isAddFrameworkOpen, setIsAddFrameworkOpen] = useState(false);
  const [editFramework, setEditFramework] = useState<ContentManagementControlFrameworkSummary | null>(null);
  const [publishFramework, setPublishFramework] = useState<ContentManagementControlFrameworkSummary | null>(null);
  const [versionHistoryFramework, setVersionHistoryFramework] = useState<ContentManagementControlFrameworkSummary | null>(null);
  const [deleteFramework, setDeleteFramework] = useState<ContentManagementControlFrameworkSummary | null>(null);
  const [detailFramework, setDetailFramework] = useState<ContentManagementControlFrameworkSummary | null>(null);
  const [deleteFrameworkControl, setDeleteFrameworkControl] = useState<{
    framework: ContentManagementControlFrameworkSummary;
    controlId: string;
  } | null>(null);

  const scenarioQuery = useQuery<ContentManagementScenarioPacksQuery>(CONTENT_MANAGEMENT_SCENARIO_PACKS, {
    skip: !canManageContent,
  });
  const frameworkQuery = useQuery<ContentManagementControlFrameworksQuery>(CONTENT_MANAGEMENT_CONTROL_FRAMEWORKS, {
    skip: !canManageContent,
  });
  const ownerOrgQuery = useFlatOrganisationsHomeQuery({
    variables: { includeArchived: false },
    skip: !canManageContent,
  });
  const [deleteScenarioGroupPack] = useMutation<
    DeleteScenarioGroupPackMutation,
    DeleteScenarioGroupPackMutationVariables
  >(DELETE_SCENARIO_GROUP_PACK);
  const [deleteScenarioGroupRisks] = useMutation<
    DeleteScenarioGroupRisksMutation,
    DeleteScenarioGroupRisksMutationVariables
  >(DELETE_SCENARIO_GROUP_RISKS);
  const [deleteControlFramework] = useMutation<
    DeleteControlFrameworkMutation,
    DeleteControlFrameworkMutationVariables
  >(DELETE_CONTROL_FRAMEWORK);
  const [deleteControlFrameworkControls] = useMutation<
    DeleteControlFrameworkControlsMutation,
    DeleteControlFrameworkControlsMutationVariables
  >(DELETE_CONTROL_FRAMEWORK_CONTROLS);

  const scenarioPacks = (scenarioQuery.data?.contentManagementScenarioPacks || []) as ScenarioPackWithDetails[];
  const frameworks = frameworkQuery.data?.contentManagementControlFrameworks || [];
  const packOwnerOptions = useMemo<PackOwnerOption[]>(() => {
    const partners = ((ownerOrgQuery.data?.flatOrganisations || []) as PublishTargetOrgUnit[])
      .filter((org) => org.recordType === 'O' && !org.isArchived && org.organisationType === 'PARTNER')
      .map(packOwnerOptionFromOrg)
      .sort((a, b) => a.label.localeCompare(b.label));

    return [{ value: CYDEA_OWNER_VALUE, label: 'Cydea', organisation: null }, ...partners];
  }, [ownerOrgQuery.data]);
  const isLoading = tab === 'scenarios' ? scenarioQuery.loading : tab === 'frameworks' ? frameworkQuery.loading : false;

  useEffect(() => {
    if (!detailGroup) return;
    const refreshed = scenarioPacks.find((group) => group.id === detailGroup.id);
    if (refreshed && refreshed !== detailGroup) setDetailGroup(refreshed);
  }, [scenarioPacks, detailGroup]);

  useEffect(() => {
    if (!detailFramework) return;
    const refreshed = frameworks.find((framework) => framework.id === detailFramework.id);
    if (refreshed && refreshed !== detailFramework) setDetailFramework(refreshed);
  }, [frameworks, detailFramework]);

  if (!canManageContent) {
    return <ErrorPage status={403} />;
  }

  if (detailGroup) {
    return (
      <>
        <Meta title="Cydea | Content Management" feedbackScript />
        <ScenarioDetailView
          group={detailGroup}
          onBack={() => setDetailGroup(null)}
          onEdit={() => setEditScenarioPack(detailGroup)}
          onPublish={() => setPublishScenarioPack(detailGroup)}
          onVersionHistory={() => setVersionHistoryGroup(detailGroup)}
          onManageAccess={() => setPublishScenarioPack(detailGroup)}
          onDeletePack={() => setDeleteGroup(detailGroup)}
          onDeleteScenario={(scenarioId) => setDeleteScenario({ group: detailGroup, scenarioId })}
        />
        {editScenarioPack && (
          <ScenarioPackDraftModal
            mode="edit"
            isOpen={Boolean(editScenarioPack)}
            group={editScenarioPack}
            onClose={() => setEditScenarioPack(null)}
            onSaved={async () => {
              await scenarioQuery.refetch();
              setEditScenarioPack(null);
            }}
          />
        )}
        {publishScenarioPack && (
          <PublishContentAccessModal
            isOpen={Boolean(publishScenarioPack)}
            type="scenario"
            item={publishScenarioPack}
            onClose={() => setPublishScenarioPack(null)}
            onPublished={async () => {
              await scenarioQuery.refetch();
              setPublishScenarioPack(null);
            }}
          />
        )}
        {versionHistoryGroup && (
          <ScenarioGroupVersionHistory
            group={versionHistoryGroup}
            onClose={() => setVersionHistoryGroup(null)}
            onChanged={async () => {
              await scenarioQuery.refetch();
              setVersionHistoryGroup(null);
            }}
          />
        )}
        {deleteGroup && (
          <ConfirmDeleteModal
            title="Are you sure you want to delete?"
            confirmText="DELETE"
            onClose={() => setDeleteGroup(null)}
            onConfirm={async () => {
              try {
                await deleteScenarioGroupPack({ variables: { scenarioGroupId: deleteGroup.id } });
                setDeleteGroup(null);
                setDetailGroup(null);
                showSuccessToast({ title: 'Scenario pack deleted' });
                await scenarioQuery.refetch();
              } catch (error: any) {
                setDeleteGroup(null);
                showErrorToast(error?.message || 'Could not delete scenario pack.');
              }
            }}
          >
            <Poppins px={14} color="cflowerBlue">
              This scenario will be deleted from the content manager permanently.
            </Poppins>
          </ConfirmDeleteModal>
        )}
        {deleteScenario && (
          <ConfirmDeleteModal
            title="Are you sure you want to delete?"
            confirmText="DELETE"
            onClose={() => setDeleteScenario(null)}
            onConfirm={async () => {
              try {
                await deleteScenarioGroupRisks({
                  variables: { scenarioGroupId: deleteScenario.group.id, scenarioIds: [deleteScenario.scenarioId] },
                });
                setDeleteScenario(null);
                showSuccessToast({ title: 'Scenario deleted' });
                await scenarioQuery.refetch();
              } catch (error: any) {
                setDeleteScenario(null);
                showErrorToast(error?.message || 'Could not delete scenario.');
              }
            }}
          >
            <Poppins px={14} color="cflowerBlue">
              This scenario will be deleted from the content manager permanently.
            </Poppins>
          </ConfirmDeleteModal>
        )}
      </>
    );
  }

  if (detailFramework) {
    return (
      <>
        <Meta title="Cydea | Content Management" feedbackScript />
        <FrameworkDetailView
          framework={detailFramework}
          onBack={() => setDetailFramework(null)}
          onEdit={() => setEditFramework(detailFramework)}
          onPublish={() => setPublishFramework(detailFramework)}
          onVersionHistory={() => setVersionHistoryFramework(detailFramework)}
          onManageAccess={() => setPublishFramework(detailFramework)}
          onDeletePack={() => setDeleteFramework(detailFramework)}
          onDeleteControl={(controlId) => setDeleteFrameworkControl({ framework: detailFramework, controlId })}
        />
        {editFramework && (
          <EditControlFrameworkModal
            isOpen={Boolean(editFramework)}
            onClose={() => setEditFramework(null)}
            frameworks={[editFramework]}
            onEdited={async () => {
              await frameworkQuery.refetch();
              setEditFramework(null);
            }}
          />
        )}
        {publishFramework && (
          <PublishContentAccessModal
            isOpen={Boolean(publishFramework)}
            type="framework"
            item={publishFramework}
            onClose={() => setPublishFramework(null)}
            onPublished={async () => {
              await frameworkQuery.refetch();
              setPublishFramework(null);
            }}
          />
        )}
        {versionHistoryFramework && (
          <ControlFrameworkVersionHistory
            framework={versionHistoryFramework}
            onClose={() => setVersionHistoryFramework(null)}
            onChanged={async () => {
              await frameworkQuery.refetch();
              setVersionHistoryFramework(null);
            }}
          />
        )}
        {deleteFramework && (
          <ConfirmDeleteModal
            title="Are you sure you want to delete?"
            confirmText="DELETE"
            onClose={() => setDeleteFramework(null)}
            onConfirm={async () => {
              try {
                await deleteControlFramework({ variables: { frameworkId: deleteFramework.id } });
                setDeleteFramework(null);
                setDetailFramework(null);
                showSuccessToast({ title: 'Control framework deleted' });
                await frameworkQuery.refetch();
              } catch (error: any) {
                setDeleteFramework(null);
                showErrorToast(error?.message || 'Could not delete control framework.');
              }
            }}
          >
            <Poppins px={14} color="cflowerBlue">
              This framework will be deleted from the content manager permanently.
            </Poppins>
          </ConfirmDeleteModal>
        )}
        {deleteFrameworkControl && (
          <ConfirmDeleteModal
            title="Are you sure you want to delete?"
            confirmText="DELETE"
            onClose={() => setDeleteFrameworkControl(null)}
            onConfirm={async () => {
              try {
                const result = await deleteControlFrameworkControls({
                  variables: {
                    frameworkId: deleteFrameworkControl.framework.id,
                    controlIds: [deleteFrameworkControl.controlId],
                  },
                });
                const nextFrameworkId = result.data?.deleteControlFrameworkControls.frameworkId;
                setDeleteFrameworkControl(null);
                showSuccessToast({ title: 'Control deleted' });
                const refreshed = await frameworkQuery.refetch();
                const nextFramework = refreshed.data?.contentManagementControlFrameworks.find(
                  (framework) => framework.id === (nextFrameworkId || deleteFrameworkControl.framework.id),
                );
                if (nextFramework) setDetailFramework(nextFramework);
              } catch (error: any) {
                setDeleteFrameworkControl(null);
                showErrorToast(error?.message || 'Could not delete control.');
              }
            }}
          >
            <Poppins px={14} color="cflowerBlue">
              This control will be deleted from the content manager permanently.
            </Poppins>
          </ConfirmDeleteModal>
        )}
      </>
    );
  }

  return (
    <>
      <Meta title="Cydea | Content Management" feedbackScript />
      <Div>
        <div className="head">
          <div className="title-row">
            <Poppins px={32} weight={600}>
              Content Management
            </Poppins>
            <AccessConfigButton
              secondary
              onClick={() => {
                if (tab === 'frameworks') {
                  const framework = frameworks[0];
                  if (!framework) {
                    showErrorToast('No content available to configure.');
                    return;
                  }

                  setPublishFramework(framework);
                  return;
                }

                const scenarioPack = scenarioPacks[0];
                if (!scenarioPack) {
                  showErrorToast('No content available to configure.');
                  return;
                }

                setPublishScenarioPack(scenarioPack);
              }}
            >
              Content Access Config
            </AccessConfigButton>
          </div>
          <div className="tabs" role="tablist" aria-label="Content management sections">
            <button type="button" className={`tab ${tab === 'scenarios' ? 'tab--active' : ''}`} onClick={() => setTab('scenarios')}>
              Scenarios
            </button>
            <button type="button" className={`tab ${tab === 'frameworks' ? 'tab--active' : ''}`} onClick={() => setTab('frameworks')}>
              Frameworks
            </button>
            <button type="button" className={`tab ${tab === 'wizards' ? 'tab--active' : ''}`} onClick={() => setTab('wizards')}>
              Wizards
            </button>
          </div>
        </div>
        {tab === 'scenarios' && (
          <>
            <SectionHeader>
              <div>
                <Poppins px={16} weight={600} color="gp1" css="text-transform: uppercase; display: block;">
                  Scenarios
                </Poppins>
                <Spacer $px={8} />
                <Poppins px={14} color="cflowerBlue">
                  Add and manage your scenario packs
                </Poppins>
              </div>
              <ActionButton primary onClick={() => setIsAddScenarioPackOpen(true)}>
                + Add Scenario Pack
              </ActionButton>
            </SectionHeader>
            <CardsGrid>
              {isLoading ? (
                <>
                  <AssessmentCardSkeleton />
                  <AssessmentCardSkeleton />
                  <AssessmentCardSkeleton />
                </>
              ) : (
                scenarioPacks.map((group) => {
                  const isDraft = getPackStatus(group.featureKey) === 'draft';

                  return (
                    <ContentCard
                      key={group.id}
                      title={group.name}
                      description={group.description}
                      countLabel={`${group.scenarios?.length || 0} ${(group.scenarios?.length || 0) === 1 ? 'Scenario' : 'Scenarios'}`}
                      branding={getPackBranding(group)}
                      versionNumber={group.versionNumber}
                      updatedAt={group.meta?.updatedAt || group.meta?.createdAt}
                      isDraft={isDraft}
                      onView={() => setDetailGroup(group)}
                      onEdit={() => setEditScenarioPack(group)}
                      onPublish={() => setPublishScenarioPack(group)}
                      onHide={() => showErrorToast('Hide content is not available yet.')}
                      onVersionHistory={() => setVersionHistoryGroup(group)}
                      onDelete={() => setDeleteGroup(group)}
                    />
                  );
                })
              )}
            </CardsGrid>
          </>
        )}
        {tab === 'frameworks' && (
          <>
            <SectionHeader>
              <div>
                <Poppins px={16} weight={600} color="gp1" css="text-transform: uppercase; display: block;">
                  Frameworks
                </Poppins>
                <Spacer $px={8} />
                <Poppins px={14} color="cflowerBlue">
                  Add and manage your framework packs
                </Poppins>
              </div>
              <ActionButton primary onClick={() => setIsAddFrameworkOpen(true)}>
                + Add Framework Pack
              </ActionButton>
            </SectionHeader>
            <CardsGrid>
              {isLoading ? (
                <>
                  <AssessmentCardSkeleton />
                  <AssessmentCardSkeleton />
                  <AssessmentCardSkeleton />
                </>
              ) : (
                frameworks.map((framework) => {
                  const isDraft = !framework.featureKey;

                  return (
                    <ContentCard
                      key={framework.id}
                      title={framework.shortname || framework.name}
                      description={framework.description || framework.name}
                      countLabel={`${framework.controlCount || 0} ${(framework.controlCount || 0) === 1 ? 'Control' : 'Controls'}`}
                      branding={getPackBranding(framework)}
                      versionNumber={framework.versionNumber}
                      updatedAt={framework.meta?.updatedAt || framework.meta?.createdAt}
                      isDraft={isDraft}
                      onView={() => setDetailFramework(framework)}
                      onEdit={() => setEditFramework(framework)}
                      onPublish={() => setPublishFramework(framework)}
                      onHide={() => showErrorToast('Hide content is not available yet.')}
                      onVersionHistory={() => setVersionHistoryFramework(framework)}
                      onDelete={() => setDeleteFramework(framework)}
                    />
                  );
                })
              )}
            </CardsGrid>
          </>
        )}
        {tab === 'wizards' && (
          <Placeholder>
            <Poppins px={18} weight={600}>
              Wizards
            </Poppins>
            <Spacer $px={8} />
            <Poppins px={14} color="cflowerBlue">
              Wizard content management will be added here as a separate organisation-agnostic content area.
            </Poppins>
          </Placeholder>
        )}
      </Div>
      {isAddScenarioPackOpen && (
        <ScenarioPackDraftModal
          mode="add"
          isOpen={isAddScenarioPackOpen}
          onClose={() => setIsAddScenarioPackOpen(false)}
          onSaved={async () => {
            await scenarioQuery.refetch();
            setIsAddScenarioPackOpen(false);
          }}
        />
      )}
      {editScenarioPack && (
        <ScenarioPackDraftModal
          mode="edit"
          isOpen={Boolean(editScenarioPack)}
          group={editScenarioPack}
          onClose={() => setEditScenarioPack(null)}
          onSaved={async () => {
            await scenarioQuery.refetch();
            setEditScenarioPack(null);
          }}
        />
      )}
      {publishScenarioPack && (
        <PublishContentAccessModal
          isOpen={Boolean(publishScenarioPack)}
          type="scenario"
          item={publishScenarioPack}
          onClose={() => setPublishScenarioPack(null)}
          onPublished={async () => {
            await scenarioQuery.refetch();
            setPublishScenarioPack(null);
          }}
        />
      )}
      {versionHistoryGroup && (
        <ScenarioGroupVersionHistory
          group={versionHistoryGroup}
          onClose={() => setVersionHistoryGroup(null)}
          onChanged={async () => {
            await scenarioQuery.refetch();
            setVersionHistoryGroup(null);
          }}
        />
      )}
      {deleteGroup && (
        <ConfirmDeleteModal
          title="Are you sure you want to delete?"
          confirmText="DELETE"
          onClose={() => setDeleteGroup(null)}
          onConfirm={async () => {
            try {
              await deleteScenarioGroupPack({ variables: { scenarioGroupId: deleteGroup.id } });
              setDeleteGroup(null);
              showSuccessToast({ title: 'Scenario pack deleted' });
              await scenarioQuery.refetch();
            } catch (error: any) {
              setDeleteGroup(null);
              showErrorToast(error?.message || 'Could not delete scenario pack.');
            }
          }}
        >
          <Poppins px={14} color="cflowerBlue">
            This scenario will be deleted from the content manager permanently.
          </Poppins>
        </ConfirmDeleteModal>
      )}
      {isAddFrameworkOpen && (
        <AddControlFrameworkModal
          isOpen={isAddFrameworkOpen}
          onClose={() => setIsAddFrameworkOpen(false)}
          ownerOptions={packOwnerOptions}
          defaultOwnerId={null}
          showOwnerSelector
          onCreated={async () => {
            await frameworkQuery.refetch();
          }}
        />
      )}
      {editFramework && (
        <EditControlFrameworkModal
          isOpen={Boolean(editFramework)}
          onClose={() => setEditFramework(null)}
          frameworks={[editFramework]}
          ownerOptions={packOwnerOptions}
          showOwnerSelector
          onEdited={async () => {
            await frameworkQuery.refetch();
            setEditFramework(null);
          }}
        />
      )}
      {publishFramework && (
        <PublishContentAccessModal
          isOpen={Boolean(publishFramework)}
          type="framework"
          item={publishFramework}
          onClose={() => setPublishFramework(null)}
          onPublished={async () => {
            await frameworkQuery.refetch();
            setPublishFramework(null);
          }}
        />
      )}
      {versionHistoryFramework && (
        <ControlFrameworkVersionHistory
          framework={versionHistoryFramework}
          onClose={() => setVersionHistoryFramework(null)}
          onChanged={async () => {
            await frameworkQuery.refetch();
            setVersionHistoryFramework(null);
          }}
        />
      )}
      {deleteFramework && (
        <ConfirmDeleteModal
          title="Are you sure you want to delete?"
          confirmText="DELETE"
          onClose={() => setDeleteFramework(null)}
          onConfirm={async () => {
            try {
              await deleteControlFramework({ variables: { frameworkId: deleteFramework.id } });
              setDeleteFramework(null);
              setDetailFramework(null);
              showSuccessToast({ title: 'Control framework deleted' });
              await frameworkQuery.refetch();
            } catch (error: any) {
              setDeleteFramework(null);
              showErrorToast(error?.message || 'Could not delete control framework.');
            }
          }}
        >
          <Poppins px={14} color="cflowerBlue">
            This framework will be deleted from the content manager permanently.
          </Poppins>
        </ConfirmDeleteModal>
      )}
    </>
  );
};

export default ContentManagementPage;
